import { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Dialog,
  Typography,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  DialogTitle
} from "@mui/material";
import CustomButton from "../Common/Button";
import BasicDataTable from "../Common/BasicDatatable";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { MRT_ColumnDef } from 'material-react-table';
import { formatDate } from "../../utils/utils";
import IntegrationService from "../../services/integrations/Integrations";
import { ClearIcon } from "@mui/x-date-pickers";

const generateRows = (data: any) => {
  return data.map((item: any) => ({
    id: item.topicId,
    topic_data: item.topicName,
    description: item.description,
    name: item.name,
    updated_at: item.updatedAt,
  }));
}
const Suggestion = () => {
  const [data, setData] = useState({ topic_data: "", description: "" });
  const [openDialog, setOpenDialog] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [editId, setEditId] = useState<string>("");
  const { showSnackbar } = useDisplaySnackbar();
  const [ errors, setErrors ] = useState({ topic_data: false, description: false});

  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "topic_data",
      header: "Topic",
      Cell: ({ row }: { row: any }) => {
        return (
          <Tooltip title={row.original.topic_data}>
            <Box className="truncate-table-cell-ellipsis">
              {row.original.topic_data}
            </Box>
          </Tooltip>
        );
      },
      size: 210,
      minSize: 210,
      enableSorting: false,
    },    
    {
      accessorKey: "description",
      header: "Description",
      Cell: ({ row }: { row: any }) => (
        <Tooltip title={row.original.description}>
          <Box className="truncate-table-cell-ellipsis">
            {row.original.description}
          </Box>
        </Tooltip>
      ),
      size: 350,
      enableSorting: false,
    },
    {
      accessorKey: "name",
      header: "User",
      enableSorting: false,
      enableColumnFilter: false,
      size: 150,
      Cell: ({ row }: { row: any }) => (
        <Box>
          {row.original.name}
        </Box>
      ),
    },
    {
      accessorKey: "updated_at",
      header: "Date",
      enableSorting: false,
      enableColumnFilter: false,
      size: 150,
      Cell: ({row}) =>  {
        return (
          <Box>
            {formatDate(row.original.updated_at)}
          </Box>
        );
      },
    },
    {
      accessorKey: "actions",
      header: "Actions",
      enableSorting: false,
      enableColumnFilter: false,
      size: 150,
      Cell: ({ row }: { row: any }) => (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Edit">
            <IconButton onClick={() => handleEdit(row.original.id)}>
              <EditIcon color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDelete(row.original.id)}>
              <DeleteIcon/>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];


  const {
    isLoading: isTopicLoading,
    isFetching,
    isSuccess,
    data: topicDataResponse,
    refetch
  } = IntegrationService.useGetAccountTopics(true);

  useEffect(() => {
    if (isSuccess) {
      const result = topicDataResponse?.data || [];
      const sortedResult = [...result].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setRows(generateRows(sortedResult));
    }
  }, [isSuccess, topicDataResponse, isTopicLoading, isFetching]);
  
  
  const { mutate: postTopic } = IntegrationService.usePostAccountTopic({
    onSuccess: () => {
      refetch();
      setOpenDialog(false);
      setData({ topic_data: "", description: "" });
      showSnackbar("success", "Topic Added Successfully");
    },
    onError: (err: any) => {
      console.error("Post failed: ", err);
    }
  });

  const { mutate: updateTopic } = IntegrationService.useUpdateAccountTopic({
    onSuccess: () => {
      refetch();
      setOpenDialog(false);
      showSnackbar("success", "Topic Updated Successfully");
    },
    onError: (err: any) => {
      console.error("Update failed: ", err);
      const errorMessage = err?.response?.data?.message || "Failed to update topic";
      showSnackbar("error", errorMessage);
    },
  });

  const { mutate: deleteTopic } = IntegrationService.useDeleteAccountTopic({
    onSuccess: () => {
      refetch();
      showSnackbar("success", "Topic Deleted Successfully");
    },
    onError: (err: any) => {
      console.error("Delete failed: ", err);
    }
  });

  useEffect(() => {
    if (openDialog) {
      setErrors({ topic_data: false, description: false });
      if (!editId) {
        setData({
          topic_data: "",
          description: "",
        });
      }
    }
  }, [openDialog]);
  
  const handleSubmit = () => {
    const topicEmpty = !data.topic_data.trim();
    const descriptionEmpty = !data.description.trim();

    setErrors({ topic_data: topicEmpty, description: descriptionEmpty});
    if (!topicEmpty && !descriptionEmpty) {
      setOpenDialog(false);
      if (editId) {
        updateTopic({
          topicId: editId,
          topicName: data.topic_data,
          description: data.description,
          isForUpdate: true,
        });
      } else {
        postTopic({
          topicName: data.topic_data,
          description: data.description,
          isForUpdate: false,
        });
      }
    }
  };

  const handleEdit = (id: number) => {
    const item = rows.find((item) => item.id === id);
    if (item) {
      setData({ topic_data: item.topic_data, description: item.description });
      setEditId(item.id);
      setOpenDialog(true);
    }
  };

  const handleDelete = (id: string) => {
      deleteTopic({topicId : id});
    };

  return (
    <Box className="w-full">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          paddingBottom: 2,
          paddingTop: 2,
        }}
      >
        <CustomButton
          type="AddNew"
          label="Add Topic"
          clickHandler={() =>{
            setEditId("");
            setData({ topic_data: "", description: "" });
            setOpenDialog(true)}}
        />
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth= "lg"
      >
        <DialogTitle sx={{ position: "relative" }}>
          <Typography variant="h5" sx={{ fontFamily: 'var(--font-family)', textAlign: "center" }}>
            {editId ? "Edit Topic" : "Add Topic"}
          </Typography>
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: '100%', sm: '700px' } }}
        >
          <Typography variant="h5" sx={{ color: "#222222", fontFamily: 'var(--font-family)' }}>
            Topic <span style={{ color: "#FF2E00", fontFamily: 'var(--font-family)', fontSize: "16px" }}>* {errors.topic_data && "Required"} </span>
          </Typography>
          <TextField
            value={data.topic_data}
            onChange={(e) => {setData({ ...data, topic_data: e.target.value });
                              setErrors((prev) => ({ ...prev, topic_data: false }));}}
            multiline
            error={errors.topic_data}
          />
          <Typography variant="h5" sx={{ color: "#222222", fontFamily: 'var(--font-family)' }}>
            Description <span style={{ color: "#FF2E00", fontFamily: 'var(--font-family)', fontSize: "16px" }}>* {errors.description && "Required"} </span>
          </Typography>
          <TextField
            value={data.description}
            onChange={(e) => {setData({ ...data, description: e.target.value });
                              setErrors((prev) => ({ ...prev, description: false }));}}
            multiline
            minRows={3}
            inputProps={{ maxLength: 550 }}
            helperText={`${data.description.length}/550`}
            error={errors.description}
          />
        </DialogContent>
        <DialogActions>
          <CustomButton
            type="SmallBlueFixed"
            label="Submit"
            clickHandler={handleSubmit}
          />
          <CustomButton
            type="SmallOutlined"
            label="Cancel"
            clickHandler={() => setOpenDialog(false)}
          />
        </DialogActions>
      </Dialog>

      <BasicDataTable
          columns={columns}
          rows={rows}
          rowCount={rows.length}
          hidePaginationFooter={true}
        />
    </Box>
  );
};

export default Suggestion;
